import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Spinner } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import API from "../services/api";
import { formatEmployeeId } from "../utils/idFormat";
import AdminUserHistoryPanel from "./AdminUserHistoryPanel";
import "./AdminEmployeeDetail.css";

const API_ORIGIN = process.env.REACT_APP_API_ORIGIN || "";

const toFileURL = (raw) => {
  if (!raw) return "";
  const value = String(raw).trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/")) return `${API_ORIGIN}${value}`;
  return `${API_ORIGIN}/uploads/${value}`;
};

const fmtDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtDateTime = (value) =>
  value ? new Date(value).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const fmtMinutes = (value) => {
  const total = Number(value || 0);
  if (!total) return "0m";
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return hours ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const statusTone = (value = "") => {
  const normalized = String(value).toLowerCase();
  if (["approved", "completed", "closed", "generated", "active", "seen", "present"].includes(normalized)) return "success";
  if (["pending", "in-review", "in-progress", "scheduled", "proposed", "unseen"].includes(normalized)) return "warning";
  if (["rejected", "cancelled", "terminated", "absent"].includes(normalized)) return "danger";
  return "neutral";
};

const KeyValue = ({ label, value }) => (
  <div className="aed-kv">
    <span>{label}</span>
    <strong>{value || "—"}</strong>
  </div>
);

const EmptyState = ({ text }) => <div className="aed-empty">{text}</div>;

const AdminEmployeeDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchAccount = async () => {
      try {
        setLoading(true);
        setError("");
        const { data } = await API.get(`/admin/employees/${id}/account`);
        if (mounted) setPayload(data);
      } catch (err) {
        if (mounted) setError(err?.response?.data?.message || "Failed to load employee account");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchAccount();
    return () => {
      mounted = false;
    };
  }, [id]);

  const employee = payload?.employee || {};
  const summary = payload?.summary || {};
  const issues = payload?.issues || {};
  const hr = payload?.hr || {};
  const employeeDisplayId = formatEmployeeId(employee);
  const requestedTab = searchParams.get("tab") || "overview";

  useEffect(() => {
    setActiveTab(requestedTab);
  }, [requestedTab]);

  const tabs = useMemo(
    () => [
      { key: "overview", label: "Overview" },
      { key: "attendance", label: `Attendance (${payload?.attendance?.length || 0})` },
      { key: "issues", label: `Issues (${(issues.reports?.length || 0) + (issues.deletionRequests?.length || 0)})` },
      { key: "requirements", label: `Requirements (${payload?.requirements?.length || 0})` },
      { key: "hr", label: `HR (${summary.hrCount || 0})` },
      { key: "notifications", label: `Alerts (${payload?.notifications?.length || 0})` },
      { key: "activity", label: `Activity (${payload?.timeline?.length || 0})` },
      { key: "history", label: "History" },
    ],
    [issues.deletionRequests?.length, issues.reports?.length, payload?.attendance?.length, payload?.notifications?.length, payload?.requirements?.length, payload?.timeline?.length, summary.hrCount]
  );

  const hrSections = [
    { key: "leaveRequests", title: "Leave Requests" },
    { key: "performanceReviews", title: "Performance Reviews" },
    { key: "payslips", title: "Payslips" },
    { key: "salaryStructures", title: "Salary Structures" },
    { key: "resignations", title: "Resignations" },
    { key: "offboardings", title: "Offboarding" },
    { key: "letters", title: "Letters" },
    { key: "exitInterviews", title: "Exit Interviews" },
    { key: "appraisals", title: "Appraisals" },
  ];

  const renderOverview = () => (
    <div className="aed-stack">
      <div className="aed-grid aed-grid-2">
        <Card className="aed-card">
          <Card.Body>
            <div className="aed-section-title">Account Snapshot</div>
            <div className="aed-kv-grid">
              <KeyValue label="Name" value={employee.fullName} />
              <KeyValue label="Employee ID" value={employeeDisplayId} />
              <KeyValue label="Mobile" value={employee.mobile} />
              <KeyValue label="Email" value={employee.email} />
              <KeyValue label="Department" value={employee.department?.name} />
              <KeyValue label="Role" value={employee.employeeRole?.name} />
              <KeyValue label="Status" value={employee.approved ? "Approved" : "Pending"} />
              <KeyValue label="Joined" value={fmtDate(employee.createdAt)} />
              <KeyValue label="Last Activity" value={fmtDateTime(summary.lastActivityAt)} />
            </div>
          </Card.Body>
        </Card>

        <Card className="aed-card">
          <Card.Body>
            <div className="aed-section-title">Work Profile</div>
            <div className="aed-kv-grid">
              <KeyValue label="Shift" value={employee.workProfile?.shiftStart && employee.workProfile?.shiftEnd ? `${employee.workProfile.shiftStart} - ${employee.workProfile.shiftEnd}` : "Not set"} />
              <KeyValue label="Attendance Mode" value={employee.workProfile?.attendanceMode || "office"} />
              <KeyValue label="Monthly Salary" value={employee.workProfile?.salaryMonthly ? `₹ ${Number(employee.workProfile.salaryMonthly).toLocaleString("en-IN")}` : "Not set"} />
              <KeyValue label="Hourly Rate" value={employee.workProfile?.hourlyRate ? `₹ ${employee.workProfile.hourlyRate}` : "—"} />
              <KeyValue label="OT Rate" value={employee.workProfile?.overtimeRate ? `₹ ${employee.workProfile.overtimeRate}` : "—"} />
              <KeyValue label="Late Grace" value={employee.workProfile?.lateGraceMinutes ? `${employee.workProfile.lateGraceMinutes} min` : "—"} />
              <KeyValue label="Office Radius" value={employee.workProfile?.officeRadiusMeters ? `${employee.workProfile.officeRadiusMeters} m` : "—"} />
              <KeyValue label="Office Address" value={employee.workProfile?.officeAddress || "Not set"} />
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="aed-grid aed-grid-3">
        <Card className="aed-card">
          <Card.Body>
            <div className="aed-section-title">Agreement & ID</div>
            <div className="aed-kv-grid">
              <KeyValue label="Agreement" value={employee.employeeAgreementSummary?.accepted ? "Signed" : "Pending"} />
              <KeyValue label="Signed By" value={employee.employeeAgreementSummary?.signName} />
              <KeyValue label="Accepted At" value={fmtDateTime(employee.employeeAgreementSummary?.acceptedAt)} />
              <KeyValue label="ID Card" value={employee.idCardStatus || "pending"} />
              <KeyValue label="Photo Status" value={employee.photoStatus || "pending"} />
            </div>
          </Card.Body>
        </Card>

        <Card className="aed-card">
          <Card.Body>
            <div className="aed-section-title">Bank Details</div>
            <div className="aed-kv-grid">
              <KeyValue label="Account Name" value={employee.bank?.accountName} />
              <KeyValue label="Account Number" value={employee.bank?.accountNumber} />
              <KeyValue label="IFSC" value={employee.bank?.ifsc} />
              <KeyValue label="Bank" value={employee.bank?.bankName} />
              <KeyValue label="Branch" value={employee.bank?.branch} />
            </div>
          </Card.Body>
        </Card>

        <Card className="aed-card">
          <Card.Body>
            <div className="aed-section-title">Assigned Vendors</div>
            {employee.assignedVendors?.length ? (
              <div className="aed-chip-wrap">
                {employee.assignedVendors.map((vendor) => (
                  <span key={vendor._id} className="aed-chip">
                    {vendor.companyName || vendor.fullName || vendor.email}
                  </span>
                ))}
              </div>
            ) : (
              <EmptyState text="No vendors assigned." />
            )}
          </Card.Body>
        </Card>

        <Card className="aed-card">
          <Card.Body>
            <div className="aed-section-title">Assigned Suppliers</div>
            {employee.assignedSuppliers?.length ? (
              <div className="aed-chip-wrap">
                {employee.assignedSuppliers.map((supplier) => (
                  <span key={supplier._id} className="aed-chip">
                    {supplier.companyName || supplier.supplierName || supplier.fullName || supplier.email}
                  </span>
                ))}
              </div>
            ) : (
              <EmptyState text="No suppliers assigned." />
            )}
          </Card.Body>
        </Card>
      </div>

      <Card className="aed-card">
        <Card.Body>
          <div className="aed-section-title">Documents</div>
          <div className="aed-doc-grid">
            {[
              { key: "aadhar", label: "Aadhar Card" },
              { key: "pan", label: "PAN Card" },
              { key: "addressProof", label: "Address Proof" },
              { key: "resume", label: "Resume" },
            ].map((doc) => {
              const url = toFileURL(employee.employeeDocuments?.[doc.key]);
              return (
                <div key={doc.key} className="aed-doc-card">
                  <div>
                    <div className="aed-doc-title">{doc.label}</div>
                    <div className="aed-doc-sub">{url ? "Uploaded" : "Not uploaded"}</div>
                  </div>
                  {url ? (
                    <a href={url} target="_blank" rel="noreferrer" className="aed-doc-link">
                      Open
                    </a>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  const renderAttendance = () =>
    payload?.attendance?.length ? (
      <div className="aed-list">
        {payload.attendance.map((item) => (
          <Card key={item.id} className="aed-card">
            <Card.Body>
              <div className="aed-row-head">
                <div>
                  <div className="aed-item-title">{fmtDate(item.date)}</div>
                  <div className="aed-item-sub">{item.address || "No address captured"}</div>
                </div>
                <span className={`aed-badge ${statusTone(item.status)}`}>{item.status}</span>
              </div>
              <div className="aed-kv-grid">
                <KeyValue label="Punch In" value={fmtDateTime(item.punchInTime)} />
                <KeyValue label="Punch Out" value={fmtDateTime(item.punchOutTime)} />
                <KeyValue label="Work Time" value={fmtMinutes(item.workMinutes)} />
                <KeyValue label="Late" value={fmtMinutes(item.lateMinutes)} />
                <KeyValue label="Overtime" value={fmtMinutes(item.overtimeMinutes)} />
                <KeyValue label="Approval" value={item.approvalStatus} />
                <KeyValue label="Mode" value={item.attendanceMode} />
                <KeyValue label="Meeting Mode" value={item.meetingMode ? "Yes" : "No"} />
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    ) : (
      <EmptyState text="Attendance records not found." />
    );

  const renderIssues = () => (
    <div className="aed-stack">
      <Card className="aed-card">
        <Card.Body>
          <div className="aed-section-title">Issue Reports</div>
          {issues.reports?.length ? (
            <div className="aed-list">
              {issues.reports.map((item) => (
                <div key={item.id} className="aed-line-card">
                  <div className="aed-row-head">
                    <div>
                      <div className="aed-item-title">{item.title}</div>
                      <div className="aed-item-sub">{item.category || "General"} | {fmtDateTime(item.createdAt)}</div>
                    </div>
                    <span className={`aed-badge ${statusTone(item.status)}`}>{item.status}</span>
                  </div>
                  <p className="aed-paragraph">{item.message || "No message added."}</p>
                  <div className="aed-kv-grid">
                    <KeyValue label="Priority" value={item.priority} />
                    <KeyValue label="Severity" value={item.severity} />
                    <KeyValue label="Vendor" value={item.vendorName} />
                    <KeyValue label="Requirement" value={item.requirement} />
                    <KeyValue label="Admin Note" value={item.adminNote || "—"} />
                    <KeyValue label="Resolution" value={item.resolution || "—"} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState text="No issue reports for this user." />
          )}
        </Card.Body>
      </Card>

      <Card className="aed-card">
        <Card.Body>
          <div className="aed-section-title">Account Deletion Requests</div>
          {issues.deletionRequests?.length ? (
            <div className="aed-list">
              {issues.deletionRequests.map((item) => (
                <div key={item.id} className="aed-line-card">
                  <div className="aed-row-head">
                    <div className="aed-item-title">{fmtDateTime(item.requestedAt)}</div>
                    <span className={`aed-badge ${statusTone(item.status)}`}>{item.status}</span>
                  </div>
                  <p className="aed-paragraph">{item.reason || "No reason added."}</p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState text="No deletion requests linked to this account." />
          )}
        </Card.Body>
      </Card>
    </div>
  );

  const renderRequirements = () =>
    payload?.requirements?.length ? (
      <div className="aed-list">
        {payload.requirements.map((item) => (
          <Card key={item.id} className="aed-card">
            <Card.Body>
              <div className="aed-row-head">
                <div>
                  <div className="aed-item-title">{item.productType}</div>
                  <div className="aed-item-sub">{item.vendorName || "Vendor not linked"} | {fmtDateTime(item.createdAt)}</div>
                </div>
                <div className="aed-row-badges">
                  <span className={`aed-badge ${statusTone(item.status)}`}>{item.status}</span>
                  <span className={`aed-badge ${statusTone(item.approvalStatus)}`}>{item.approvalStatus}</span>
                </div>
              </div>
              <div className="aed-kv-grid">
                <KeyValue label="Quantity" value={`${item.quantity} ${item.unit}`} />
                <KeyValue label="Dispatch" value={item.dispatchStatus} />
                <KeyValue label="Location" value={item.location || "—"} />
                <KeyValue label="Notes" value={item.notes || "—"} />
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    ) : (
      <EmptyState text="No assigned requirements found." />
    );

  const renderHr = () => (
    <div className="aed-stack">
      {hrSections.map((section) => {
        const items = hr?.[section.key] || [];
        return (
          <Card key={section.key} className="aed-card">
            <Card.Body>
              <div className="aed-section-title">{section.title}</div>
              {items.length ? (
                <div className="aed-list">
                  {items.map((item) => (
                    <div key={item.id} className="aed-line-card">
                      <div className="aed-row-head">
                        <div>
                          <div className="aed-item-title">{item.title || section.title}</div>
                          <div className="aed-item-sub">{fmtDateTime(item.date || item.createdAt)}</div>
                        </div>
                        <span className={`aed-badge ${statusTone(item.status)}`}>{item.status}</span>
                      </div>
                      <p className="aed-paragraph">{item.summary || item.reason || "No extra details."}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState text={`No ${section.title.toLowerCase()} found.`} />
              )}
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );

  const renderNotifications = () =>
    payload?.notifications?.length ? (
      <div className="aed-list">
        {payload.notifications.map((item) => (
          <Card key={item.id} className="aed-card">
            <Card.Body>
              <div className="aed-row-head">
                <div className="aed-item-title">{fmtDateTime(item.createdAt)}</div>
                <span className={`aed-badge ${statusTone(item.seen ? "seen" : "unseen")}`}>{item.seen ? "Seen" : "Unseen"}</span>
              </div>
              <p className="aed-paragraph">{item.message}</p>
            </Card.Body>
          </Card>
        ))}
      </div>
    ) : (
      <EmptyState text="No notifications sent to this user." />
    );

  const renderActivity = () =>
    payload?.timeline?.length ? (
      <div className="aed-list">
        {payload.timeline.map((item) => (
          <Card key={item.key} className="aed-card">
            <Card.Body>
              <div className="aed-row-head">
                <div>
                  <div className="aed-item-title">{item.title}</div>
                  <div className="aed-item-sub">{fmtDateTime(item.at)} | {item.tab}</div>
                </div>
                <span className={`aed-badge ${statusTone(item.status)}`}>{item.status}</span>
              </div>
              <p className="aed-paragraph">{item.detail || "No extra detail."}</p>
            </Card.Body>
          </Card>
        ))}
      </div>
    ) : (
      <EmptyState text="No activity timeline generated yet." />
    );

  const tabBody = {
    overview: renderOverview(),
    attendance: renderAttendance(),
    issues: renderIssues(),
    requirements: renderRequirements(),
    hr: renderHr(),
    notifications: renderNotifications(),
    activity: renderActivity(),
    history: <AdminUserHistoryPanel role="employee" userId={id} />,
  };

  if (loading) {
    return (
      <div className="aed-shell aed-loading">
        <Spinner animation="border" />
        <div>Loading employee account...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="aed-shell">
        <Alert variant="danger">{error}</Alert>
        <Button variant="dark" onClick={() => navigate("/admin/employees")}>
          Back to Employees
        </Button>
      </div>
    );
  }

  return (
    <div className="aed-shell">
      <div className="aed-hero">
        <div className="aed-hero-left">
          <button type="button" className="aed-back" onClick={() => navigate("/admin/employees")}>
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <div className="aed-avatar">
            {employee.photo ? <img src={toFileURL(employee.photo)} alt={employee.fullName || "Employee"} /> : <span>{String(employee.fullName || "EM").slice(0, 2).toUpperCase()}</span>}
          </div>
          <div>
            <div className="aed-title">{employee.fullName || "Employee Account"}</div>
            <div className="aed-subtitle">
              {employee.department?.name || "No department"} | {employee.employeeRole?.name || "No role"} | {employee.mobile || "No mobile"}
            </div>
          </div>
        </div>
        <div className="aed-hero-right">
          <span className={`aed-badge ${employee.approved ? "success" : "warning"}`}>{employee.approved ? "Approved" : "Pending"}</span>
          <span className={`aed-badge ${statusTone(employee.idCardStatus)}`}>ID Card: {employee.idCardStatus || "pending"}</span>
        </div>
      </div>

      <div className="aed-summary-grid">
        <div className="aed-summary-card">
          <span>Attendance</span>
          <strong>{summary.attendanceCount || 0}</strong>
          <small>{summary.presentCount || 0} present records</small>
        </div>
        <div className="aed-summary-card">
          <span>Open Issues</span>
          <strong>{summary.openIssueCount || 0}</strong>
          <small>{summary.reportCount || 0} total reports</small>
        </div>
        <div className="aed-summary-card">
          <span>Requirements</span>
          <strong>{summary.requirementCount || 0}</strong>
          <small>{summary.activeRequirementCount || 0} active</small>
        </div>
        <div className="aed-summary-card">
          <span>Notifications</span>
          <strong>{summary.notificationCount || 0}</strong>
          <small>{summary.unseenNotificationCount || 0} unseen</small>
        </div>
        <div className="aed-summary-card">
          <span>HR Records</span>
          <strong>{summary.hrCount || 0}</strong>
          <small>{summary.leaveCount || 0} leave requests</small>
        </div>
        <div className="aed-summary-card">
          <span>Audit Logs</span>
          <strong>{summary.auditCount || 0}</strong>
          <small>Latest {fmtDateTime(summary.lastActivityAt)}</small>
        </div>
      </div>

      <div className="aed-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`aed-tab ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => {
              setActiveTab(tab.key);
              setSearchParams(tab.key === "overview" ? {} : { tab: tab.key }, { replace: true });
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="aed-body">{tabBody[activeTab]}</div>
    </div>
  );
};

export default AdminEmployeeDetail;
